import {
  notification,
  Row,
  Col,
  Empty,
  Segmented,
  Badge,
  Switch,
  Pagination,
  Input,
  Button,
} from "antd";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../assets/utils/useAuth";
import { usePermissions } from "../../assets/utils/usePermissions";
import useLocalStorage from "../../assets/utils/useLocal";
import { useDispatch, useSelector } from "react-redux";
import "../../styles/main.scss";
import config from "../../assets/utils/config";
import {
  LoadingOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  SyncOutlined,
  AppstoreOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { selectclient } from "../../stores/verify";
import Ribbon from "antd/es/badge/Ribbon";
import { VerifyClientCard } from "./ClientListingCard";
import { VerifyEnrolleeCard } from "./EnrolleeListingCard";
import lib from "../../assets/utils/lib";
import DashboardHero from "../dashboard/components/DashboardHero";
import DashboardSection from "../dashboard/components/DashboardSection";
import { TableSkeleton } from "../dashboard/components/DashboardSkeleton";
import { SearchResultsSkeleton } from "../search/SearchResultCards";
import { hasMetricValue } from "../dashboard/utils";
import VerifyQueueTable from "./VerifyQueueTable";
import ArchiveClientRequestsModal from "../../components/Claims/ArchiveClientRequestsModal";
import { canManageClientRequestsArchive } from "../../components/Claims/claimsArchiveHelpers";
import "../dashboard/index.css";
import "./verify-dashboard.css";

const { Search } = Input;

const ClientVerufyHook = (props) => {
  const { user } = useAuth();
  const { canStayOnModule, hasAnyPermissionWithLegacy } = usePermissions();
  const navigate = useNavigate();

  const dispatcher = useDispatch(selectclient);

  const [loading, setLoading] = useState(false);
  
  // Dark mode state - synced with navigation theme system
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('sidebarDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const activeSeg = useRef(0);
  const activeProfileType = useRef(true);
  const [adminScope, setAdminScope] = useState('client');

  const [activeSegment, setActiveSegment] = useLocalStorage('new');
  const [viewMode, setViewMode] = useLocalStorage('verify-view-mode-v2', 'table');

  const [activeSegState, setActiveSegState] = useState(
    activeSegment ? activeSegment : 'new'
  );

  // Whether the verification scope is user-selectable (the Policies / Enrollees toggle)
  // rather than pinned. Legacy Super User (1) kept, permission-first.
  const canToggleVerificationScope = hasAnyPermissionWithLegacy(
    ['admin.*', 'clients.manage_all'],
    [1],
  );

  /**
   * Single resolver for WHICH RECORD SET the verification queue reads — the `tag` sent to
   * POST ?unverified, which selects the clients table or the client_biodata table. Used by
   * both usreg() (the table) and fetchFilterCounts() (the hero badges); the two must never
   * describe different record sets.
   *
   * Permission-first, with the legacy staff role ids applying only on an empty permission
   * map. Two substitutions, because the obvious permissions are seeded to nobody:
   *  - 'clients.update' stands in for enrollees.read_all / enrollees.manage_all so a seeded
   *    Customer Care account (legacy role 2) stays on the ENROLLEE queue instead of flipping
   *    to the policy queue via its clients.read_all.
   *  - 'users.read_all' stands in for the unseeded clients.verify so a seeded Head of Finance
   *    account (legacy role 5) keeps the client/policy queue; it is already part of the
   *    clientVerification module's anyOf, so those accounts can reach this page regardless.
   */
  const verificationScope = useCallback(() => {
    if (canToggleVerificationScope) return adminScope;
    if (hasAnyPermissionWithLegacy(['enrollees.read_all', 'enrollees.manage_all', 'clients.update'], [2])) {
      return 'enrollee';
    }
    if (hasAnyPermissionWithLegacy(['clients.read_all', 'clients.verify', 'users.read_all'], [5])) {
      return 'client';
    }
    return null;
  }, [canToggleVerificationScope, adminScope, hasAnyPermissionWithLegacy]);

  const usreg = useCallback(() => {
    if (!user) {
      // setTag(null);
      return;
    }

    const scope = verificationScope();
    return scope ? `${scope}-${activeSegState}` : null;
  }, [user, activeSegState, verificationScope]);

  // Determines which category/segment to request from the API
  const [tag, setTag] = useState(() => usreg());

  const [dataList, setDataList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState({ perPage: 12, total: 0 });
  const [searchText, setSearchText] = useState("");
  const [clientArchiveModalOpen, setClientArchiveModalOpen] = useState(false);
  const [clientArchiveModalMode, setClientArchiveModalMode] = useState('archive');
  const canArchiveClientRequests = canManageClientRequestsArchive(user);
  
  // Filter counts state
  const [filterCounts, setFilterCounts] = useState({
    new: 0,
    updated: 0,
    total: 0
  });
  
  // Mobile responsiveness state  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    setTag(usreg());
  }, [activeSegState, usreg]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for theme changes from navigation
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('sidebarDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };

    // Listen for storage events (cross-tab)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom theme change events
    const handleThemeChange = () => {
      const saved = localStorage.getItem('sidebarDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    
    window.addEventListener('themeChanged', handleThemeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsSmallMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch filter counts for badges
  const fetchFilterCounts = useCallback(async () => {
    if (!user) return;

    try {
      // Same resolver as usreg(), so the badges and the table always describe one record set.
      const baseTag = verificationScope();

      if (!baseTag) return;

      // Fetch counts for both filters using the existing API structure
      const [newResponse, updatedResponse] = await Promise.all([
        lib.unverifiedClients({ tag: `${baseTag}-new`, page: 1, perPage: 1, search: "" }),
        lib.unverifiedClients({ tag: `${baseTag}-updated`, page: 1, perPage: 1, search: "" })
      ]);

      const newCount = newResponse?.data?.pagination?.total || 0;
      const updatedCount = updatedResponse?.data?.pagination?.total || 0;

      setFilterCounts({
        new: newCount,
        updated: updatedCount,
        total: newCount + updatedCount
      });
    } catch (error) {
      console.error("Error fetching filter counts:", error);
    }
  }, [user, verificationScope]);

  const fetchData = useCallback(async (page = 1, query = "") => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!canStayOnModule('clientVerification', [1, 2, 5])) {
      notification.warning({
        message: 'Access Denied',
        description: "You don't have permission to verify clients.",
        placement: 'topLeft',
      });
      navigate('/dashboard');
      return;
    }
    setLoading(true);
    const reqData = await lib.unverifiedClients({ tag, page, perPage: paginationInfo?.perPage || 12, search: query });
    const { data, pagination } = reqData?.data || {};
    if (!data?.error) {
      const { records } = data
      const list = Array.isArray(records?.[activeSegState]) ? records?.[activeSegState] : [];
      setDataList(list);
      setPaginationInfo(pagination || {});
    }
    setLoading(false);
  }, [user, tag, paginationInfo?.perPage, activeSegState, navigate, canStayOnModule]);

  // Fetch filter counts on mount and user change
  useEffect(() => {
    if (user) {
      fetchFilterCounts();
    }
  }, [user, fetchFilterCounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // initial & tag change
  useEffect(() => {
    fetchData(1, searchText);
    setCurrentPage(1);
  }, [user, tag, activeSegState, fetchData, searchText]);

  // page change
  useEffect(() => {
    fetchData(currentPage, searchText);
  }, [currentPage, fetchData, searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  // search change handled onSearch

  const handleMenuClick = (e) => {
    activeSeg.current = e;
    setActiveSegState(e);
    setActiveSegment(e);
    setCurrentPage(1);
  };

  const refreshAll = useCallback(() => {
    fetchFilterCounts();
    fetchData(currentPage, searchText);
  }, [fetchFilterCounts, fetchData, currentPage, searchText]);

  const isEnrolleeScope = tag?.startsWith('enrollee');
  const isClientScope = tag?.startsWith('client');
  const scopeNoun = isEnrolleeScope ? 'enrollees' : 'policies';
  const scopeLabel = isEnrolleeScope ? 'Enrollees' : 'Policies';
  const queueTitle = isEnrolleeScope
    ? (activeSegState === 'new' ? 'New enrollee entries' : 'Enrollee renewals')
    : (activeSegState === 'new' ? 'New policy entries' : 'Policy renewals');

  const heroMetrics = useMemo(() => {
    const viewingNew = activeSegState === 'new';
    return [
      {
        key: 'new',
        title: `New ${scopeNoun}`,
        value: filterCounts.new,
        color: '#3497D3',
        icon: isEnrolleeScope ? <TeamOutlined /> : <UserOutlined />,
        subtitle: viewingNew ? 'Viewing this queue' : 'Pending verification',
        onClick: () => handleMenuClick('new'),
      },
      {
        key: 'updated',
        title: 'Renewals',
        value: filterCounts.updated,
        color: '#49B870',
        icon: <SyncOutlined />,
        subtitle: !viewingNew ? 'Viewing this queue' : `${scopeLabel} updates`,
        onClick: () => handleMenuClick('updated'),
      },
      {
        key: 'total',
        title: `Total ${scopeNoun}`,
        value: filterCounts.total,
        color: '#722ed1',
        icon: <UnorderedListOutlined />,
        subtitle: `All pending ${scopeNoun}`,
      },
      {
        key: 'results',
        title: 'In this view',
        value: paginationInfo?.total || 0,
        color: '#faad14',
        icon: <SearchOutlined />,
        subtitle: searchText ? `"${searchText}"` : `Page ${currentPage}`,
      },
    ].filter((m) => hasMetricValue(m.value) || m.key === 'results');
  }, [activeSegState, filterCounts, paginationInfo?.total, searchText, currentPage, isEnrolleeScope, scopeNoun, scopeLabel]);

  const runSearch = () => {
    setCurrentPage(1);
    fetchData(1, searchText);
  };

  return (
    <div className="verify-dashboard">
      {user ? (
        <div className="dashboard-page-stack">
          <header className="dash-masthead">
            <div className="dash-masthead__lead">
              <h1 className="dash-masthead__title">Verification</h1>
              <p className="dash-masthead__meta">
                <span>Review pending registrations and renewals</span>
              </p>
            </div>
          </header>

          <section className="verify-overview dash-overview dash-overview--compact" aria-label="Queue overview">
            <DashboardHero metrics={heroMetrics} loading={loading && !dataList.length && !filterCounts.total} />
          </section>

          {canToggleVerificationScope ? (
            <div className="dash-tab-strip" role="tablist" aria-label="Verification scope">
              <Segmented
                className="verify-scope-toggle"
                value={adminScope}
                onChange={(value) => {
                  activeProfileType.current = value === 'client';
                  setAdminScope(value);
                  setCurrentPage(1);
                }}
                options={[
                  { label: 'Policies', value: 'client', icon: <UserOutlined /> },
                  { label: 'Enrollees', value: 'enrollee', icon: <TeamOutlined /> },
                ]}
              />
            </div>
          ) : null}

          <DashboardSection
            id="verify-queue"
            icon={<UnorderedListOutlined />}
            title={queueTitle}
            subtitle={viewMode === 'table' ? 'Click a row to review and verify' : 'Click a card to review and verify'}
            collapsible={false}
            variant="content"
            action={(
              <div className="verify-toolbar verify-toolbar--inline">
                <Input
                  className="verify-toolbar__search"
                  size="small"
                  placeholder={`Search ${isEnrolleeScope ? 'enrollees' : 'clients'}…`}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onPressEnter={runSearch}
                  allowClear
                  disabled={loading}
                />
                <Button
                  type="primary"
                  size="small"
                  icon={<SearchOutlined />}
                  onClick={runSearch}
                  loading={loading}
                >
                  Search
                </Button>
                {dataList.length > 0 || loading ? (
                  <Segmented
                    className="verify-view-toggle"
                    size="small"
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      { label: 'Cards', value: 'grid', icon: <AppstoreOutlined /> },
                      { label: 'Table', value: 'table', icon: <TableOutlined /> },
                    ]}
                  />
                ) : null}
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={refreshAll}
                  loading={loading}
                >
                  Refresh
                </Button>
                {canArchiveClientRequests ? (
                  <Button
                    size="small"
                    onClick={() => {
                      setClientArchiveModalMode('archive');
                      setClientArchiveModalOpen(true);
                    }}
                  >
                    Archive resolved
                  </Button>
                ) : null}
              </div>
            )}
          >
            {loading && !dataList.length ? (
              viewMode === 'table' ? (
                <TableSkeleton rows={8} columns={isClientScope ? 8 : 7} />
              ) : (
                <SearchResultsSkeleton count={6} />
              )
            ) : dataList.length === 0 ? (
              <div className="verify-empty">
                <div className="verify-empty__icon"><UserOutlined /></div>
                <p className="verify-empty__title">No {activeSegState} entries found</p>
                <p className="verify-empty__desc">
                  {searchText
                    ? `No results for "${searchText}". Try adjusting your search.`
                    : `No ${activeSegState} entries are currently pending verification.`}
                </p>
              </div>
            ) : (
              <>
                {viewMode === 'table' ? (
                  <VerifyQueueTable data={dataList} isClientScope={isClientScope} />
                ) : (
                  <div className="verify-cards-grid verify-cards-grid--linear">
                    {dataList.map((item) =>
                      isClientScope ? (
                        <VerifyClientCard key={item.id} data={item} />
                      ) : (
                        <VerifyEnrolleeCard key={item.id} data={item} />
                      )
                    )}
                  </div>
                )}
                {(paginationInfo?.total || 0) > (paginationInfo?.perPage || 12) ? (
                  <div className="verify-pagination">
                    <Pagination
                      current={currentPage}
                      pageSize={paginationInfo?.perPage || 12}
                      total={paginationInfo?.total || 0}
                      onChange={(page) => setCurrentPage(page)}
                      showSizeChanger={false}
                      showTotal={(total, range) =>
                        `Showing ${range[0]}-${range[1]} of ${total} entries`
                      }
                    />
                  </div>
                ) : null}
              </>
            )}
          </DashboardSection>
        </div>
      ) : (
        <div className="verify-unauthorized">
          <WarningOutlined style={{ fontSize: 40, marginBottom: 12 }} />
          <div className="verify-empty__title">Access required</div>
          <p className="verify-empty__desc">Please log in to access the verification system.</p>
        </div>
      )}
      {user ? (
        <ArchiveClientRequestsModal
          open={clientArchiveModalOpen}
          mode={clientArchiveModalMode}
          onClose={() => setClientArchiveModalOpen(false)}
          onSuccess={() => {
            setClientArchiveModalOpen(false);
            refreshAll();
          }}
        />
      ) : null}
    </div>
  );
};


export default ClientVerufyHook;
